/**
 * O ⓘ de cada premissa (onda 313, R14; spec 07 §5.1): o grupo da prancheta, a fórmula e a referência —
 * transcritas dos cabeçalhos do motor (`basico.ts`, `correlacoes.ts`, `dissipacao.ts`, `conformidade.ts`):
 * nenhuma fórmula que o motor não tenha.
 *
 * `naTela` é a classificação da TELA, não o `efeito` da conformidade: só para ler («sem efeito no motor
 * 1.0.0») ficam as premissas que o motor 1.0.0 não usa — Ic de corte, Qtn,cs limite, Q, grau de carga do
 * Es, pré-furo — e o ΔIc: na conformidade ele é valor exigido (0), mas o motor não o usa, e com outro valor
 * o CPeT-IT daria outro resultado e o motor o mesmo; deixá-lo mudar seria mentir na tela.
 *
 * O que a prancheta (e a spec §5.1, no φ) dizia e o motor desmente se corrige aqui: Su, OCR, St e K₀ valem
 * nas zonas SBTn 1, 2, 3, 4 e 9 (a aplicabilidade segue a zona, não o Ic de corte); φ' é o de Robertson
 * (2022) por Qtn,cs, com piso no φ'cv; o Ir é G₀/Su da própria vertical; o NA e a cota são da vertical
 * (o cabeçalho), não exceção.
 */
import type { Grandeza, Premissas } from '../motor';

/** Os grupos da aba Premissas (a prancheta 4). */
export type GrupoDePremissa =
  | 'Cone e água' | 'Peso específico e normalização' | 'Argilas' | 'Areias' | 'Deformabilidade e SPT' | 'Dissipação';

/** O ⓘ de uma premissa. */
export interface FormulaDaPremissa {
  chave: keyof Premissas;
  grupo: GrupoDePremissa;
  rotulo: string;
  unidade: string;
  /** A fórmula em que a premissa entra, como o motor a calcula. */
  formula: string;
  /** De onde vem a fórmula (ou a regra do motor, quando é escolha dele). */
  referencia: string;
  /** Onde a fórmula vale (as zonas SBTn, o solo saturado); null quando vale em toda leitura. */
  aplicabilidade: string | null;
  /** Na tela: muda (recalcula) ou só para ler (sem efeito no motor 1.0.0). */
  naTela: 'muda' | 'so_leitura';
  /** É da vertical (o cabeçalho e a importação), não da campanha: NA, cota e pré-furo. */
  daVertical: boolean;
}

/** A frase das premissas que o motor 1.0.0 não usa. */
const SEM_EFEITO = 'sem efeito no motor 1.0.0';

const LISTA_DO_CPETIT = 'lista de fórmulas do CPeT-IT (a última folha do relatório 2609350)';
const ARGILOSAS = 'zonas SBTn 1, 2, 3, 4 e 9 (argilosas)';
const ARENOSAS = 'zonas SBTn 5 a 8 (arenosas)';

type Entrada = Omit<FormulaDaPremissa, 'chave' | 'naTela' | 'daVertical'> & Partial<Pick<FormulaDaPremissa, 'naTela' | 'daVertical'>>;

const ENTRADAS: Record<keyof Premissas, Entrada> = {
  /* ── Cone e água ── */
  a: {
    grupo: 'Cone e água', rotulo: 'Razão de área líquida do cone, a', unidade: '',
    formula: 'qt = qc + u₂·(1 − a)/1000 (qc e qt em MPa, u₂ em kPa)',
    referencia: LISTA_DO_CPETIT, aplicabilidade: null,
  },
  gamaW: {
    grupo: 'Cone e água', rotulo: 'Peso específico da água, γw', unidade: 'kN/m³',
    formula: 'u₀ = γw·(z − NA), ≥ 0; kₕ = cₕ·γw/M',
    referencia: LISTA_DO_CPETIT, aplicabilidade: null,
  },
  u0Modo: {
    grupo: 'Cone e água', rotulo: 'Perfil de u₀', unidade: '',
    formula: 'hidrostático: u₀ = γw·(z − NA), ≥ 0; por pontos: a interpolação linear entre os pontos (z, u₀)',
    referencia: `${LISTA_DO_CPETIT}; o perfil por pontos é do motor CPTu 1.0.0 (basico.ts)`, aplicabilidade: null,
  },
  u0Pontos: {
    grupo: 'Cone e água', rotulo: 'Pontos do perfil de u₀', unidade: 'm × kPa',
    formula: 'u₀(z) = a interpolação linear entre os pontos (z, u₀); acima do primeiro e abaixo do último, o da ponta',
    referencia: 'motor CPTu 1.0.0 (basico.ts)', aplicabilidade: 'só com o perfil de u₀ por pontos',
  },
  na: {
    grupo: 'Cone e água', rotulo: 'Nível d\'água (NA)', unidade: 'm',
    formula: 'u₀ = γw·(z − NA), ≥ 0',
    referencia: LISTA_DO_CPETIT,
    aplicabilidade: 'da vertical (o cabeçalho), não exceção; w, e e γd só com z ≥ NA', daVertical: true,
  },
  cota: {
    grupo: 'Cone e água', rotulo: 'Cota da boca da vertical', unidade: 'm',
    formula: 'cota da leitura = cota da boca − z',
    referencia: 'motor CPTu 1.0.0 (camadas.ts: o perfil por cota)',
    aplicabilidade: 'da vertical (o cabeçalho), não exceção', daVertical: true,
  },
  preFuro: {
    grupo: 'Cone e água', rotulo: 'Pré-furo', unidade: 'm',
    formula: `${SEM_EFEITO}: a primeira leitura leva o peso da coluna acima (σv₁ = γ₁·z₁) com ou sem pré-furo; `
      + 'o trecho do pré-furo sai por um corte da edição',
    referencia: 'motor CPTu 1.0.0 (basico.ts, regra bas.sv-primeira-leitura; edicao.ts)',
    aplicabilidade: 'da vertical (vem da importação)', naTela: 'so_leitura', daVertical: true,
  },

  /* ── Peso específico e normalização ── */
  gamaModo: {
    grupo: 'Peso específico e normalização', rotulo: 'Peso específico γ', unidade: '',
    formula: 'automático: γ = γw·(0,27·log Rf + 0,36·log(qt/pa) + 1,236), pa = 101,325 kPa; fixo: γ = γ fixo',
    referencia: 'Robertson & Cabal (2010); pa = 101,325 kPa: regra bas.pa-101325', aplicabilidade: null,
  },
  gamaFixo: {
    grupo: 'Peso específico e normalização', rotulo: 'γ fixo', unidade: 'kN/m³',
    formula: 'γ = γ fixo; com o γ automático, a reserva onde a fórmula não dá número',
    referencia: 'motor CPTu 1.0.0 (basico.ts, regra bas.gama-reserva)', aplicabilidade: null,
  },
  gamaLimitar: {
    grupo: 'Peso específico e normalização', rotulo: 'Limite do γ', unidade: '',
    formula: 'γ ≥ 1,4·γw (o piso; teto não há)',
    referencia: 'comportamento copiado do CPeT-IT (basico.ts, regra bas.gama-piso)', aplicabilidade: null,
  },
  cnCorte: {
    grupo: 'Peso específico e normalização', rotulo: 'Corte de Cn', unidade: '',
    formula: 'Cn = (pa/σ\'v)ⁿ, limitado ao corte quando ligado; Qtn = ((qt − σv)/pa)·Cn',
    referencia: 'Robertson (2009)', aplicabilidade: null,
  },
  icCorte: {
    grupo: 'Peso específico e normalização', rotulo: 'Ic de corte', unidade: '',
    formula: `${SEM_EFEITO}: a aplicabilidade das correlações segue a zona SBTn, e a classificação, os ábacos`,
    referencia: 'motor CPTu 1.0.0 (correlacoes.ts, escolha 1)', aplicabilidade: null, naTela: 'so_leitura',
  },
  deltaIc: {
    grupo: 'Peso específico e normalização', rotulo: 'ΔIc', unidade: '',
    formula: `${SEM_EFEITO}: o motor calcula como com ΔIc = 0 (o CPeT-IT daria outro resultado), e só com 0 a `
      + 'interpretação é homologada',
    referencia: 'motor CPTu 1.0.0 (correlacoes.ts, escolha 1; conformidade.ts)', aplicabilidade: null,
    naTela: 'so_leitura',
  },
  mediaMovel: {
    grupo: 'Peso específico e normalização', rotulo: 'Média móvel', unidade: 'leituras',
    formula: 'qc, fs e u₂ das contas = a média móvel centrada de N leituras (a ponta repete a leitura da ponta); '
      + 'a tabela mostra as leituras',
    referencia: 'comportamento copiado do CPeT-IT (basico.ts, regra bas.media-movel-nas-contas)',
    aplicabilidade: 'soma-se ao filtro da edição: os dois se aplicam',
  },

  /* ── Argilas ── */
  nkt: {
    grupo: 'Argilas', rotulo: 'Nkt', unidade: '',
    formula: 'Su = (qt − σv)/Nkt',
    referencia: `Robertson & Cabal (2012); ${LISTA_DO_CPETIT}`, aplicabilidade: ARGILOSAS,
  },
  nktPorFr: {
    grupo: 'Argilas', rotulo: 'Nkt por Fr', unidade: '',
    formula: 'Nkt = 10,5 + 7·log Fr',
    referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS,
  },
  ns: {
    grupo: 'Argilas', rotulo: 'Ns', unidade: '',
    formula: 'St = Ns/Fr',
    referencia: `Robertson & Cabal (2012); ${LISTA_DO_CPETIT}`, aplicabilidade: ARGILOSAS,
  },
  nu: {
    grupo: 'Argilas', rotulo: 'Nu', unidade: '',
    formula: 'Su = Δu₂/Nu = U₂·σ\'v/Nu (com o Su por Nu ligado)',
    referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS,
  },
  suPorNu: {
    grupo: 'Argilas', rotulo: 'Su por Nu', unidade: '',
    formula: 'Su = Δu₂/Nu no lugar de Su = (qt − σv)/Nkt',
    referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS,
  },
  kocr: {
    grupo: 'Argilas', rotulo: 'kOCR', unidade: '',
    formula: 'OCR = kOCR·Qtn',
    referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS,
  },
  kocrAutomatico: {
    grupo: 'Argilas', rotulo: 'kOCR automático', unidade: '',
    formula: 'kOCR = [Qtn^0,20/(0,25·(10,5 + 7·log Fr))]^1,25',
    referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS,
  },
  phiPicoPadrao: {
    grupo: 'Argilas', rotulo: 'φ\' de pico padrão', unidade: '°',
    formula: 'K₀ = (1 − sen φ\'p)·OCR^(sen φ\'p); onde o φ\'p não se aplica (Bq fora de 0,1–1), o φ\' de pico padrão',
    referencia: 'comportamento copiado do CPeT-IT (correlacoes.ts, regra cor.k0-phi-pico-padrao)',
    aplicabilidade: `${ARGILOSAS}: o K₀`,
  },

  /* ── Areias ── */
  cdr: {
    grupo: 'Areias', rotulo: 'Constante CDr da densidade relativa', unidade: '',
    formula: 'Dr = 100·√(Qtn/CDr), ≤ 100 %',
    referencia: `Robertson & Cabal (2012); ${LISTA_DO_CPETIT}`, aplicabilidade: ARENOSAS,
  },
  phiCv: {
    grupo: 'Areias', rotulo: 'φ\'cv', unidade: '°',
    formula: 'φ\' = φ\'cv + 15,84·log Qtn,cs − 26,88, ≥ φ\'cv; Qtn,cs = Kc·Qtn',
    referencia: 'Robertson (2022), eq. 6; Robertson & Cabal (2022); o piso no φ\'cv: regra cor.phi-piso-cv',
    aplicabilidade: ARENOSAS,
  },
  qtncsLimite: {
    grupo: 'Areias', rotulo: 'Qtn,cs limite', unidade: '',
    formula: `${SEM_EFEITO}: o Qtn,cs = Kc·Qtn não tem teto`,
    referencia: 'motor CPTu 1.0.0 (correlacoes.ts, escolha 13)', aplicabilidade: null, naTela: 'so_leitura',
  },
  q: {
    grupo: 'Areias', rotulo: 'Q', unidade: '',
    formula: `${SEM_EFEITO}: nenhuma coluna usa o Q do .cpt`,
    referencia: 'motor CPTu 1.0.0 (conformidade.ts)', aplicabilidade: null, naTela: 'so_leitura',
  },

  /* ── Deformabilidade e SPT ── */
  esGrauCarga: {
    grupo: 'Deformabilidade e SPT', rotulo: 'Grau de carga do Es', unidade: '',
    formula: `${SEM_EFEITO}: Es = 0,015·10^(0,55·Ic + 1,68)·(qt − σv), sem fator de grau de carga`,
    referencia: 'motor CPTu 1.0.0 (correlacoes.ts, escolha 5)', aplicabilidade: ARENOSAS, naTela: 'so_leitura',
  },
  alfaMMax: {
    grupo: 'Deformabilidade e SPT', rotulo: 'αM máximo', unidade: '',
    formula: 'M = αM·(qt − σv); αM = mín(Qtn, 14) com Ic > 2,20, ou 0,0188·10^(0,55·Ic + 1,68); αM ≤ αM máximo',
    referencia: 'Robertson (2009); o αM máximo nos dois ramos: regra cor.alfa-m-maximo', aplicabilidade: null,
  },
  gs: {
    grupo: 'Deformabilidade e SPT', rotulo: 'Densidade relativa dos grãos, Gs', unidade: '',
    formula: 'e = (Gs·γw − γ)/(γ − γw); w = 100·e/Gs; γd = Gs·γw/(1 + e)',
    referencia: 'Barounis & Philpot (2017)', aplicabilidade: 'solo saturado: z ≥ NA',
  },

  /* ── Dissipação ── */
  t50Fator: {
    grupo: 'Dissipação', rotulo: 'T*₅₀', unidade: '',
    formula: 'cₕ = T*₅₀·r²·√Ir/t₅₀; Ir = G₀/Su da própria vertical (ou digitados)',
    referencia: 'Teh & Houlsby (1991)', aplicabilidade: '0,245 para o u₂',
  },
  raioCone: {
    grupo: 'Dissipação', rotulo: 'Raio do cone, r', unidade: 'm',
    formula: 'cₕ = T*₅₀·r²·√Ir/t₅₀',
    referencia: 'Teh & Houlsby (1991)', aplicabilidade: '1,83 cm (o do .cpt); a geometria de 10 cm² dá 1,78 cm',
  },
};

/** O ⓘ de cada uma das 31 premissas do motor, no grupo da prancheta. */
export const FORMULAS_DAS_PREMISSAS: Readonly<Record<keyof Premissas, FormulaDaPremissa>> = Object.freeze(
  Object.fromEntries(
    (Object.keys(ENTRADAS) as (keyof Premissas)[]).map((chave) => {
      const e = ENTRADAS[chave];
      return [chave, Object.freeze({ ...e, chave, naTela: e.naTela ?? 'muda', daVertical: e.daVertical ?? false })];
    }),
  ) as Record<keyof Premissas, FormulaDaPremissa>,
);

/* ── As equações das grandezas (onda 315, R6) ─────────────────────────────────────────────────────── */

/**
 * A equação de uma grandeza homologada, para o anexo de equações e as notas das folhas do relatório (315) e do
 * Portal (317): a fórmula como o motor a calcula, de onde ela vem e onde vale.
 */
export interface EquacaoDaGrandeza {
  /** A fórmula, como o motor a calcula (o cabeçalho do arquivo dele). */
  formula: string;
  /** De onde vem a fórmula (a lista do CPeT-IT, a referência publicada ou a regra do motor). */
  referencia: string;
  /** Onde a fórmula vale (as zonas SBTn, o solo saturado); null quando vale em toda leitura. */
  aplicabilidade: string | null;
}

const ROBERTSON_2022 = 'Robertson (2022), Can. Geotech. J., doi:10.1139/cgj-2020-0657';
const SATURADO = 'solo saturado: z ≥ NA (a leitura no NA incluída)';
const COM_ZONA = 'leituras com zona SBTn (1 a 9)';

/**
 * As equações das 40 grandezas homologadas do motor 1.0.0 (`CONFORMIDADE.colunas`, situação `homologada`),
 * transcritas dos cabeçalhos do motor (`basico.ts` e `correlacoes.ts`): nenhuma fórmula que o motor não tenha.
 * As leituras do equipamento (profundidade, qc, fs, u₂) não são conta e não entram; nem o que não é homologado —
 * N1(60) e kOCR (sem gabarito) e a porosidade (não confere) ficam fora até serem conferidos (RN-252).
 *
 * O que a prancheta dizia e o motor desmente se corrige aqui, como no ⓘ das premissas: Su, Su/σ'v, OCR, St e K₀
 * nas zonas SBTn 1, 2, 3, 4 e 9 (a aplicabilidade segue a zona, não o Ic de corte); φ' de Robertson (2022) por
 * Qtn,cs, com piso no φ'cv.
 */
export const EQUACOES_DAS_GRANDEZAS: Readonly<Partial<Record<Grandeza, EquacaoDaGrandeza>>> = Object.freeze(
  Object.fromEntries(
    (Object.entries({
      /* ── O básico (basico.ts) ── */
      qt: {
        formula: 'qt = qc + u₂·(1 − a)/1000 (qc e qt em MPa, u₂ em kPa)',
        referencia: LISTA_DO_CPETIT, aplicabilidade: null,
      },
      rf: { formula: 'Rf = 100·fs/qt (%)', referencia: LISTA_DO_CPETIT, aplicabilidade: null },
      gama: {
        formula: 'γ = γw·(0,27·log Rf + 0,36·log(qt/pa) + 1,236), γ ≥ 1,4·γw; pa = 101,325 kPa',
        referencia: 'Robertson & Cabal (2010); o piso: regra bas.gama-piso', aplicabilidade: null,
      },
      sv: {
        formula: 'σv = Σ γ·Δz; a primeira leitura, σv₁ = γ₁·z₁',
        referencia: `${LISTA_DO_CPETIT}; a primeira leitura: regra bas.sv-primeira-leitura`, aplicabilidade: null,
      },
      u0: { formula: 'u₀ = γw·(z − NA), ≥ 0', referencia: LISTA_DO_CPETIT, aplicabilidade: null },
      svl: { formula: 'σ\'v = σv − u₀', referencia: LISTA_DO_CPETIT, aplicabilidade: null },
      qt1: { formula: 'Qt1 = (qt − σv)/σ\'v', referencia: 'Robertson (2009)', aplicabilidade: null },
      fr: { formula: 'Fr = 100·fs/(qt − σv) (%)', referencia: 'Robertson (2009)', aplicabilidade: null },
      bq: { formula: 'Bq = (u₂ − u₀)/(qt − σv)', referencia: 'Robertson (2009)', aplicabilidade: null },
      n: {
        formula: 'n = 0,381·Ic + 0,05·(σ\'v/pa) − 0,15, n ≤ 1 (iterado a partir de 1)',
        referencia: 'Robertson (2009); a iteração: regra bas.n-iteracao', aplicabilidade: null,
      },
      cn: { formula: 'Cn = (pa/σ\'v)ⁿ, pa = 101,325 kPa', referencia: 'Robertson (2009)', aplicabilidade: null },
      ic: {
        formula: 'Ic = √((3,47 − log Qtn)² + (log Fr + 1,22)²)', referencia: 'Robertson (2009)', aplicabilidade: null,
      },
      qtn: { formula: 'Qtn = ((qt − σv)/pa)·Cn', referencia: 'Robertson (2009)', aplicabilidade: null },
      icSbt: {
        formula: 'Ic SBT = √((3,47 − log(qt/pa))² + (log Rf + 1,22)²)', referencia: 'Robertson (2010)', aplicabilidade: null,
      },
      U2: { formula: 'U₂ = (u₂ − u₀)/σ\'v', referencia: LISTA_DO_CPETIT, aplicabilidade: null },
      ib: { formula: 'I(B) = 100·(Qtn + 10)/(70 + Qtn·Fr)', referencia: 'Robertson (2016)', aplicabilidade: null },

      /* ── As correlações (correlacoes.ts) ── */
      k: {
        formula: 'k = 10^(0,952 − 3,04·Ic) com 1 < Ic ≤ 3,27; k = 10^(−4,52 − 1,37·Ic) com 3,27 < Ic ≤ 4 (m/s)',
        referencia: LISTA_DO_CPETIT, aplicabilidade: COM_ZONA,
      },
      ch: {
        formula: 'cₕ = k·M/γw (o do perfil; o da dissipação é o de Teh & Houlsby)',
        referencia: LISTA_DO_CPETIT, aplicabilidade: COM_ZONA,
      },
      n60: {
        formula: 'N60 = (qt/pa)/10^(1,1268 − 0,2817·Ic)', referencia: 'Robertson & Cabal (2012)', aplicabilidade: null,
      },
      m: {
        formula: 'M = αM·(qt − σv); αM = mín(Qtn, 14) com Ic > 2,20, ou 0,0188·10^(0,55·Ic + 1,68); αM ≤ αM máximo',
        referencia: 'Robertson (2009); o αM máximo nos dois ramos: regra cor.alfa-m-maximo', aplicabilidade: null,
      },
      dr: {
        formula: 'Dr = 100·√(Qtn/CDr), ≤ 100 %',
        referencia: `Robertson & Cabal (2012); ${LISTA_DO_CPETIT}; o teto: regra cor.dr-teto`, aplicabilidade: ARENOSAS,
      },
      phi: {
        formula: 'φ\' = φ\'cv + 15,84·log Qtn,cs − 26,88, ≥ φ\'cv',
        referencia: `${ROBERTSON_2022}, eq. 6; Robertson & Cabal (2022); o piso no φ'cv: regra cor.phi-piso-cv`,
        aplicabilidade: ARENOSAS,
      },
      es: {
        formula: 'Es = 0,015·10^(0,55·Ic + 1,68)·(qt − σv)', referencia: LISTA_DO_CPETIT, aplicabilidade: ARENOSAS,
      },
      g0: {
        formula: 'G₀ = ρ·Vs², ρ = γ/g (g = 9,81 m/s²)', referencia: 'Robertson (2009)', aplicabilidade: null,
      },
      nkt: { formula: 'Nkt = o Nkt fixo das premissas (Su = (qt − σv)/Nkt)', referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS },
      su: {
        formula: 'Su = (qt − σv)/Nkt', referencia: `Robertson & Cabal (2012); ${LISTA_DO_CPETIT}`, aplicabilidade: ARGILOSAS,
      },
      suRazao: { formula: 'Su/σ\'v', referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS },
      ocr: { formula: 'OCR = kOCR·Qtn, kOCR informado', referencia: LISTA_DO_CPETIT, aplicabilidade: ARGILOSAS },
      vs: {
        formula: 'Vs = [αvs·(qt − σv)/pa]^0,5, αvs = 10^(0,55·Ic + 1,68)', referencia: 'Robertson (2009)', aplicabilidade: null,
      },
      psi: {
        formula: 'ψ = 0,56 − 0,33·log Qtn,cs', referencia: 'Robertson & Cabal (2022)', aplicabilidade: ARENOSAS,
      },
      k0: {
        formula: 'K₀ = (1 − sen φ\'p)·OCR^(sen φ\'p); onde o φ\'p não se aplica, o φ\' de pico padrão',
        referencia: `${LISTA_DO_CPETIT}; o φ' de pico padrão: regra cor.k0-phi-pico-padrao`, aplicabilidade: ARGILOSAS,
      },
      st: {
        formula: 'St = Ns/Fr', referencia: `Robertson & Cabal (2012); ${LISTA_DO_CPETIT}`, aplicabilidade: ARGILOSAS,
      },
      phiPico: {
        formula: 'φ\'p = 29,5·Bq^0,121·(0,256 + 0,336·Bq + log Qt1), entre 20° e 40°',
        referencia: `${LISTA_DO_CPETIT}; os limites: regra cor.phi-pico-limites`, aplicabilidade: '0,1 < Bq < 1, em qualquer zona',
      },
      resistencia: {
        formula: 'o Su nas zonas SBTn 1, 2, 3, 4 e 9; σ\'v·tan φ\' nas zonas SBTn 5 a 8',
        referencia: LISTA_DO_CPETIT, aplicabilidade: COM_ZONA,
      },
      kc: {
        formula: 'Kc = 1 com Ic ≤ 1,70; acima, o polinômio de 5º grau em Ic (Ic limitado a 4,06)',
        referencia: `${ROBERTSON_2022}, eq. 4a; os coeficientes: regra cor.kc-coeficientes; o Ic até 4,06: regra cor.kc-ic-teto`,
        aplicabilidade: null,
      },
      qtncs: { formula: 'Qtn,cs = Kc·Qtn', referencia: `${ROBERTSON_2022}, eq. 2`, aplicabilidade: null },
      suR: {
        formula: 'su(liq)/σ\'v = fs/σ\'v com Ic > 3; 0,0007·e^(0,084·Qtn,cs) + 0,3/Qtn,cs com Ic ≤ 3 (limitada por tan φ\' '
          + 'nas zonas 5 a 8 e com Qtn,cs ≤ 80 nas demais)',
        referencia: `${ROBERTSON_2022}, eq. 5 e 7; as regras cor.suliq-fs-da-leitura, cor.suliq-tan-phi e cor.suliq-qtncs-80`,
        aplicabilidade: null,
      },
      w: { formula: 'w = 100·e/Gs (%)', referencia: 'Barounis & Philpot (2017)', aplicabilidade: SATURADO },
      e: { formula: 'e = (Gs·γw − γ)/(γ − γw)', referencia: 'Barounis & Philpot (2017)', aplicabilidade: SATURADO },
      gamaD: { formula: 'γd = Gs·γw/(1 + e)', referencia: 'Barounis & Philpot (2017)', aplicabilidade: SATURADO },
    }) as [Grandeza, EquacaoDaGrandeza][]).map(([g, e]) => [g, Object.freeze({ ...e })]),
  ) as Partial<Record<Grandeza, EquacaoDaGrandeza>>,
);
